import React, {useEffect, useMemo, useState} from 'react';
import {FileRejection, useDropzone} from 'react-dropzone';
import {connect} from 'react-redux';
import {compose} from 'redux';
import colors from '../../util/colors';
import {Icons} from '../../util/icons';
import {strings} from '../../util/strings';
import {createGUID} from '../../util/utility';
import AppButton from '../app-button';
import {AppFormCheckboxField} from '../forms';
import Icon from '../icon';
import {Column, Row} from '../layout';
import AppFilePreview from './app-file-preview';
import attachfilesgrey from '../../assets/attach-files-grey.svg';
import * as fonts from '../../util/fonts';
import {useMediaQuery} from 'react-responsive';

import './styles.scss';
import NewErrorMessage from '../new-error-message';

interface AppDragDropFileInputProps {
  files: any[];
  setFiles(values: any[]): void;
  labels: typeof strings.pt.createRefund;
  labelsMessages: typeof strings.pt.messages;
  checkbox?: boolean;
  checkboxPreRefund?: boolean;
  hidePreviews?: boolean;
  preAuth?: boolean;
  newFilesName?: string;
  onFileAdd?(files: any): void;
  customResponsiveButton?(): void;
  name?: any;
  filesSizeLimit?: boolean;
  globalStyles: any;
}

function AppDragDropFileInput({
  files,
  setFiles,
  labels,
  labelsMessages,
  checkbox = false,
  checkboxPreRefund = false,
  hidePreviews = false,
  newFilesName = '',
  onFileAdd,
  customResponsiveButton,
  preAuth,
  name,
  filesSizeLimit,
  globalStyles,
}: AppDragDropFileInputProps) {
  const [uploadedFiles, setUploadedFiles] = useState(files);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [hasError, setError] = useState<boolean>(false);

  const isResponsive = useMediaQuery({maxDeviceWidth: 768});

  const [createRefundLabels, setCreateRefundLabels] = useState({
    ...strings.pt.createRefund,
    ...labels,
  });

  const maxSize = 10485760;
  const [totalSize, setTotalSize] = useState(0);

  const {
    open,
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    noClick: true,
    noKeyboard: true,
    accept: preAuth ? '' : 'image/*, .pdf',
    maxSize: maxSize,
    onDropRejected: (fileRejectionArray: FileRejection[]) => {
      let errorCodes: any[] = [];

      for (let i: number = 0; i < fileRejectionArray.length; i++) {
        const fileRejection = fileRejectionArray[i];
        errorCodes = errorCodes.concat(
          fileRejection.errors.map((error) => error.code)
        );
      }

      const filteredCodes = [...new Set(errorCodes)];

      if (filteredCodes.includes('file-invalid-type')) {
        setError(true);
        return setErrorMessage('Formato de documento não suportado');
      }
      if (filteredCodes.includes('file-too-large')) {
        setError(true);
        return setErrorMessage(labelsMessages.document_sizeAlert);
      }
      if (filteredCodes.includes('files-too-large')) {
        setError(true);
        return setErrorMessage(labelsMessages.documents_sizeAlert);
      }
    },
    onDrop: async (acceptedFiles, rejectedFiles) => {
      let newFiles = [...files];
      let addedFilesId: any[] = [];

      if (filesSizeLimit) {
        const total = acceptedFiles.reduce(
          (acc, file) => acc + file.size,
          totalSize
        );

        if (total > maxSize) {
          const rejectionError: FileRejection = {
            errors: [
              {
                code: 'files-too-large',
                message: labelsMessages.documents_sizeAlert,
              },
            ],
            file: null as unknown as File,
          };

          rejectedFiles.push(rejectionError);
          return;
        }

        setTotalSize(total);
      }

      await Promise.all(
        acceptedFiles.map((file: any, index: number) => {
          setError(false);
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => {
              const binaryStr = reader.result;
              let newFile = {
                file,
                ...file,
                preview: reader.result,
                id: createGUID(),
                name: newFilesName,
                path: file.name.replace(/ /g, ''),
              };

              addedFilesId.push(newFile.id);
              newFiles.push(newFile);
            };
            reader.readAsDataURL(file);
            reader.onloadend = () => resolve(reader.result);
          });
        })
      );
      if (onFileAdd) {
        onFileAdd(addedFilesId);
      }
      setUploadedFiles(newFiles);
      setFiles(newFiles);
    },
  });

  const activeStyle = {
    borderColor: colors.black,
  };

  const acceptStyle = {
    borderColor: colors.fluorescentBlue,
  };

  const rejectStyle = {
    borderColor: colors.alertRed,
  };

  const style = useMemo(
    () => ({
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept]
  );

  const onDelete = (id: string) => {
    let newFiles = [...files];
    newFiles = newFiles.filter((elem) => {
      if (elem.id !== id) {
        return true;
      } else {
        URL.revokeObjectURL(elem.preview);
        let newTotalSize = totalSize - elem.file.size;
        setTotalSize(newTotalSize);
        return false;
      }
    });
    setUploadedFiles(newFiles);
    setFiles(newFiles);
    setError(false);
  };

  const renderCustomButton = () => {
    if (customResponsiveButton) {
      return (
        <>
          <Row onClick={() => open()}>
            {customResponsiveButton()}
            <input {...getInputProps()} />
          </Row>
        </>
      );
    } else {
      return (
        <AppButton
          title="Ok"
          className="responsive-button"
          variant="inverted"
          onClick={() => open()}>
          {createRefundLabels.createRefund_selectFilesButtonText}
          <input {...getInputProps()} />
        </AppButton>
      );
    }
  };

  const renderUploadedFiles = () =>
    uploadedFiles.map((file: any) => {
      return (
        <div className="app-drag-drop-preview-responsive">
          <AppFilePreview
            key={file.id}
            name={file.name}
            file={file}
            files={uploadedFiles}
            onDelete={() => onDelete(file.id)}
            scope="createRefund"
          />
        </div>
      );
    });

  const renderNoDocCheckbox = () => {
    return (
      <>
        {checkbox && (
          <div
            style={{
              textAlign: 'center',
            }}>
            <AppFormCheckboxField
              style={{
                justifyContent: 'center',
                paddingTop: 30,
              }}
              name={'noDocuments'}
              label={
                <span style={{...fonts.SM, paddingTop: 2}}>
                  {createRefundLabels.createRefund_checkboxClinicDocuments}
                </span>
              }
            />
          </div>
        )}
        {checkboxPreRefund && (
          <div
            style={{
              textAlign: 'center',
              marginTop: 15,
            }}>
            <AppFormCheckboxField
              style={{
                justifyContent: 'center',
                paddingTop: 30,
              }}
              name={'noDocuments'}
              label={
                <span style={{...fonts.SM}}>
                  {
                    createRefundLabels.createRefund_preComparticipationAttachmentsCheckbox
                  }
                </span>
              }
            />
          </div>
        )}
      </>
    );
  };

  return (
    <React.Fragment>
      <Column className="app-drag-drop-container">
        {hasError ? (
          <NewErrorMessage
            text={errorMessage}
            iconName={Icons.ALERT_PROBLEM}
            colorIcon={colors.alertRed}
          />
        ) : null}

        {hidePreviews ? null : (
          <Row className="app-drag-drop-preview-row">
            {renderUploadedFiles()}
          </Row>
        )}

        <section className="container">
          <>
            {!isResponsive && (
              <div
                {...getRootProps({
                  className:
                    checkboxPreRefund || checkbox
                      ? 'dropzone-checkbox'
                      : 'dropzone',
                  style,
                })}
                onClick={open}>
                <Column className="app-drag-drop-file-input">
                  {/* <Icon
              width="60"
              height="68"
              changeViewbox={false}
              name={Icons.DECLARATIONS}
              className="app-drag-drop-icon"
            /> */}
                  <Row className="drag-drop-image-container">
                    <img
                      className="app-drag-drop-icon"
                      src={attachfilesgrey}
                      style={{marginBottom: '15px', marginTop: '15px'}}
                    />
                    <div className="drag-drop-text">
                      <span style={{...fonts.M}}>
                        {`${createRefundLabels.createRefund_selectFilesText_browser_1} `}{' '}
                      </span>
                      <span
                        style={{
                          ...fonts.M,
                          color: globalStyles?.primaryColor || colors.blue,
                        }}>{`${createRefundLabels.createRefund_selectFilesText_browser_2}`}</span>
                    </div>
                  </Row>
                  {/* <Row className="app-drag-drop-text" style={fonts.MM}></Row> */}
                  {/* <Row>
              <AppButton
                className="app-drag-drop-select-button"
                variant="secondary"
                onClick={open}>
                {labels.createRefund_selectFilesButtonText}
              </AppButton>
            </Row> */}
                  <input {...getInputProps()} />

                  {renderNoDocCheckbox()}
                </Column>
              </div>
            )}
            {isResponsive && renderCustomButton()}
          </>
        </section>
      </Column>
    </React.Fragment>
  );
}

const mapStateToProps = ({strings, stringsDefault, globalStyles}: any) => ({
  labels: strings.value.pt.createRefund,
  labelsMessages: stringsDefault.value.pt.messages,
  globalStyles: globalStyles.value,
});

export default compose(connect(mapStateToProps)(AppDragDropFileInput));
